import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Users, MoreHorizontal, Trash2, ArrowRight, LayoutGrid, ListChecks, Calendar as CalendarIcon, Upload, RefreshCw, CheckSquare, Square, X as XIcon, Flame, Snowflake, ThermometerSun, Car, Utensils, Scissors, Dumbbell, ShoppingBag, Heart, Hammer, Briefcase, Building2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PipelineKanban from "@/components/sales/PipelineKanban";
import LeadDetailModal from "@/components/sales/LeadDetailModal";
import TodayView from "@/components/sales/TodayView";
import CSVImportWizard from "@/components/sales/CSVImportWizard";
import LostReasonModal from "@/components/sales/LostReasonModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  company_name: "", contact_name: "", contact_email: "", contact_phone: "",
  website: "", google_maps_url: "", address: "", city: "",
  message: "", budget: "", source: "manuell", tags: "",
};

export default function LeadsPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("kanban"); // kanban | list | today
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("alle");
  const [filterOwner, setFilterOwner] = useState("_all");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detailLeadId, setDetailLeadId] = useState(null);
  const [pendingLostMove, setPendingLostMove] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tagsList, setTagsList] = useState([]);
  const [filterTag, setFilterTag] = useState("_all");
  const [filterScore, setFilterScore] = useState("_all");
  const [filterCity, setFilterCity] = useState("_all");

  // ── Industry-Filter (separat von filterTag, weil Industry-Tags besondere
  // Behandlung brauchen: Pill-Row, Icons, Count-Badge). Wert ist der Industry-Key
  // oder "_all". Wenn gesetzt, override-t filterTag.
  const [filterIndustry, setFilterIndustry] = useState("_all");

  // Industry-Definitionen für Pills (Label + Icon + Color)
  const INDUSTRY_META = useMemo(() => ({
    fahrschule:     { label: "Fahrschulen",   icon: Car,         color: "amber"   },
    restaurant:     { label: "Gastronomie",   icon: Utensils,    color: "orange"  },
    beauty:         { label: "Beauty",        icon: Scissors,    color: "pink"    },
    "beauty-salon": { label: "Salon",         icon: Scissors,    color: "pink"    },
    friseur:        { label: "Friseur",       icon: Scissors,    color: "pink"    },
    fitness:        { label: "Fitness",       icon: Dumbbell,    color: "green"   },
    "fitness-studio": { label: "Fitness",     icon: Dumbbell,    color: "green"   },
    supermarkt:     { label: "Einzelhandel",  icon: ShoppingBag, color: "blue"    },
    physiotherapie: { label: "Praxen",        icon: Heart,       color: "red"     },
    wellness:       { label: "Wellness",      icon: Heart,       color: "rose"    },
    anwalt:         { label: "Kanzleien",     icon: Briefcase,   color: "slate"   },
    handwerk:       { label: "Handwerk",      icon: Hammer,      color: "stone"   },
    hotel:          { label: "Hotellerie",    icon: Building2,   color: "indigo"  },
    "creative-agency": { label: "Agenturen",  icon: Sparkles,    color: "purple"  },
    "modern-tech":  { label: "Tech / SaaS",   icon: Sparkles,    color: "cyan"    },
  }), []);

  // Industry-Counts berechnen (aus tagsList — die Liste die /api/leads/tags liefert)
  const industryCounts = useMemo(() => {
    const out = {};
    (tagsList || []).forEach(t => {
      if (INDUSTRY_META[t.name]) {
        out[t.name] = (out[t.name] || 0) + t.count;
      }
    });
    return out;
  }, [tagsList, INDUSTRY_META]);

  // Sichtbare Industry-Pills: nur jene mit ≥1 Lead, sortiert nach Count desc
  const visibleIndustries = useMemo(() => {
    return Object.entries(industryCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, count, ...INDUSTRY_META[key] }));
  }, [industryCounts, INDUSTRY_META]);

  // Stadt aus Lead extrahieren — Reihenfolge: explizites city-Feld → aus Adresse parsen
  // Adress-Pattern: "Strasse 12, 01099 Dresden, Sachsen" oder "01099 Dresden"
  const extractCity = (lead) => {
    const c = (lead.city || "").trim();
    if (c) return c;
    const addr = (lead.address || "").trim();
    if (!addr) return "";
    // Pattern: 5-stellige PLZ + Leerzeichen + Stadt (deutsch, evtl. mit Bindestrich/Umlaut)
    const m = addr.match(/\b\d{5}\s+([A-ZÄÖÜ][a-zäöüß\-\.\s]+?)(?:,|$)/);
    if (m) return m[1].trim();
    return "";
  };

  // Distinct city list aus allen leads alphabetisch (case-insensitiv-dedupliziert)
  const cityOptions = useMemo(() => {
    const seen = new Map();
    for (const l of leads) {
      const c = extractCity(l);
      if (!c) continue;
      const key = c.toLowerCase();
      if (!seen.has(key)) seen.set(key, c);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "de"));
  }, [leads]);
  const [sortBy, setSortBy] = useState("score");

  const reload = useCallback(async () => {
    try {
      const [l, s, u, o, t] = await Promise.all([
        axios.get(`${API}/leads?sort=${sortBy}`, { withCredentials: true }),
        axios.get(`${API}/pipeline/stages`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/pipeline/overview`, { withCredentials: true }),
        axios.get(`${API}/leads/tags`, { withCredentials: true }),
      ]);
      setLeads(l.data);
      setStages(s.data);
      setUsers(u.data);
      setOverview(o.data);
      setTagsList(t.data);
    } catch (err) {
      if (err.response?.status !== 401) console.error(err);
    } finally { setLoading(false); }
  }, [sortBy]);

  useEffect(() => { reload(); }, [reload]);

  const stageById = useMemo(() => Object.fromEntries(stages.map(s => [s.id, s])), [stages]);
  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      const q = search.trim().toLowerCase();
      const matchSearch = !q
        || l.company_name?.toLowerCase().includes(q)
        || l.contact_name?.toLowerCase().includes(q)
        || l.contact_email?.toLowerCase().includes(q)
        || l.city?.toLowerCase().includes(q)
        || l.address?.toLowerCase().includes(q);
      const matchStage = filterStage === "alle" || l.stage_id === filterStage;
      const matchOwner = filterOwner === "_all" || (filterOwner === "_mine" ? l.assigned_to === user?.id : filterOwner === "_unassigned" ? !l.assigned_to : l.assigned_to === filterOwner);
      const matchTag = filterTag === "_all" || (l.tags || []).includes(filterTag);
      const matchIndustry = filterIndustry === "_all" || (l.tags || []).includes(filterIndustry);
      const matchScore = filterScore === "_all" || l.score_category === filterScore;
      const matchCity = filterCity === "_all" || extractCity(l).toLowerCase() === filterCity.toLowerCase();
      return matchSearch && matchStage && matchOwner && matchTag && matchIndustry && matchScore && matchCity;
    });
  }, [leads, search, filterStage, filterOwner, filterTag, filterIndustry, filterScore, filterCity, user]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const exitBulkMode = () => { setBulkMode(false); setSelectedIds(new Set()); };
  const selectAllVisible = () => setSelectedIds(new Set(filteredLeads.map(l => l.id)));

  const bulkDispatch = async (action, value) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error("Keine Leads ausgewählt"); return; }
    try {
      const r = await axios.post(`${API}/leads/bulk`, { lead_ids: ids, action, value }, { withCredentials: true });
      toast.success(`${r.data.affected} Leads bearbeitet (${action})`);
      exitBulkMode();
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const handleRefreshAllScores = async () => {
    if (!window.confirm("Alle Lead-Scores neu berechnen?\n\nMit Website-Check (langsam, aber genau) oder ohne?")) return;
    const withWeb = window.confirm("MIT Website-Check (kann pro Lead 4s dauern)?\n\nOK = mit, Abbrechen = ohne (schnell)");
    toast.info("Berechne... (kann dauern)");
    try {
      const r = await axios.post(`${API}/leads/refresh-scores?with_website_check=${withWeb}`, {}, { withCredentials: true });
      toast.success(`${r.data.refreshed} Scores aktualisiert`);
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Tags als Komma-getrennten String → Array konvertieren
      const payload = { ...form };
      if (typeof payload.tags === "string") {
        payload.tags = payload.tags.split(",").map(t => t.trim()).filter(Boolean);
      }
      await axios.post(`${API}/leads`, payload, { withCredentials: true });
      toast.success("Lead angelegt");
      setShowCreate(false);
      setForm(emptyForm);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleStageMove = async (leadId, stageId) => {
    const newStage = stages.find(s => s.id === stageId);
    if (newStage?.is_lost) {
      setPendingLostMove({ leadId, stageId });
      return;
    }
    try {
      await axios.put(`${API}/leads/${leadId}/stage`, { stage_id: stageId, lost_reason: null }, { withCredentials: true });
      toast.success(`→ ${newStage?.name}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleLostConfirmed = async (reason) => {
    if (!pendingLostMove) return;
    try {
      await axios.put(`${API}/leads/${pendingLostMove.leadId}/stage`, { stage_id: pendingLostMove.stageId, lost_reason: reason }, { withCredentials: true });
      toast.success("Als verloren markiert");
      setPendingLostMove(null);
      reload();
    } catch (err) { toast.error("Fehler"); setPendingLostMove(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Lead inklusive Verlauf löschen?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
      toast.success("Lead gelöscht");
      reload();
    } catch (err) { toast.error("Fehler"); }
  };

  const field = (key, label, type = "text") => (
    <div className="space-y-1">
      <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rounded-sm" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-[1600px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Sales-Pipeline</p>
          <h1 className="font-heading text-2xl font-black tracking-tight">Leads</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleRefreshAllScores} variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="refresh-scores-btn" title="Alle Lead-Scores aktualisieren">
            <RefreshCw size={13} /> Scores
          </Button>
          <Button data-testid="bulk-mode-btn" onClick={() => bulkMode ? exitBulkMode() : setBulkMode(true)} variant={bulkMode ? "default" : "outline"} className="rounded-sm gap-1.5" size="sm">
            {bulkMode ? <><XIcon size={14} /> Bulk beenden</> : <><CheckSquare size={14} /> Mehrfach</>}
          </Button>
          <Button data-testid="import-csv-btn" onClick={() => setShowImport(true)} variant="outline" className="rounded-sm gap-1.5" size="sm">
            <Upload size={14} /> Import
          </Button>
          <Button data-testid="new-lead-btn" onClick={() => setShowCreate(true)} className="rounded-sm gap-1.5" size="sm">
            <Plus size={14} /> Neuer Lead
          </Button>
        </div>
      </div>

      {/* Bulk-Toolbar */}
      {bulkMode && (
        <div className="bg-foreground text-background rounded-sm p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-toolbar">
          <span className="text-xs font-bold uppercase tracking-widest">{selectedIds.size} ausgewählt</span>
          <button onClick={selectAllVisible} className="text-xs underline opacity-80 hover:opacity-100" data-testid="select-all-btn">Alle sichtbaren auswählen</button>
          <div className="flex-1" />
          {/* Stage-Move */}
          <Select onValueChange={(v) => bulkDispatch("stage", v)}>
            <SelectTrigger className="w-40 h-8 text-xs rounded-sm bg-background text-foreground" data-testid="bulk-stage-select"><SelectValue placeholder="→ Stage" /></SelectTrigger>
            <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}><span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: s.color }} />{s.name}</SelectItem>)}</SelectContent>
          </Select>
          {/* Owner */}
          {user?.role === "admin" && (
            <Select onValueChange={(v) => bulkDispatch("assign", v)}>
              <SelectTrigger className="w-40 h-8 text-xs rounded-sm bg-background text-foreground" data-testid="bulk-assign-select"><SelectValue placeholder="→ Owner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_unassign">— nicht zuweisen —</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {/* Tag */}
          <Input placeholder="Tag hinzufügen…" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { bulkDispatch("tag_add", e.target.value.trim()); e.target.value = ""; } }} className="w-40 h-8 text-xs rounded-sm bg-background text-foreground" data-testid="bulk-tag-input" />
          {user?.role === "admin" && (
            <Button onClick={() => { if (window.confirm(`${selectedIds.size} Leads inkl. Verlauf löschen?`)) bulkDispatch("delete"); }} variant="destructive" size="sm" className="rounded-sm h-8 gap-1" data-testid="bulk-delete-btn">
              <Trash2 size={12} /> Löschen
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border flex gap-1">
        {[
          { k: "kanban", l: "Pipeline", icon: LayoutGrid },
          { k: "list", l: "Liste", icon: ListChecks },
          { k: "today", l: "Heute", icon: CalendarIcon },
        ].map(t => {
          const I = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2 -mb-px ${tab === t.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${t.k}`}
            >
              <I size={13} /> {t.l}
            </button>
          );
        })}
      </div>

      {/* Industry-Pills: prominent kategorisiert, 1-Klick-Filter */}
      {tab !== "today" && visibleIndustries.length > 0 && (
        <div className="flex gap-1.5 flex-wrap items-center" data-testid="industry-pills-row">
          <button
            onClick={() => setFilterIndustry("_all")}
            data-testid="industry-pill-all"
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
              filterIndustry === "_all"
                ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-transparent shadow-sm"
                : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-border hover:border-slate-400 dark:hover:border-slate-600"
            }`}
          >
            <Users size={12} className="opacity-70"/>
            Alle
            <span className={`text-[10px] ml-0.5 font-mono ${filterIndustry === "_all" ? "opacity-80" : "opacity-50"}`}>
              {leads.length}
            </span>
          </button>
          {visibleIndustries.map(ind => {
            const Icon = ind.icon;
            const active = filterIndustry === ind.key;
            const colorMap = {
              amber:  "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 ring-amber-500",
              orange: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40 ring-orange-500",
              pink:   "bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/40 ring-pink-500",
              green:  "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/40 ring-green-500",
              blue:   "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40 ring-blue-500",
              red:    "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40 ring-red-500",
              rose:   "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40 ring-rose-500",
              slate:  "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40 ring-slate-500",
              stone:  "bg-stone-500/15 text-stone-700 dark:text-stone-300 border-stone-500/40 ring-stone-500",
              indigo: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/40 ring-indigo-500",
              purple: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/40 ring-purple-500",
              cyan:   "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/40 ring-cyan-500",
            }[ind.color] || "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40 ring-slate-500";
            return (
              <button
                key={ind.key}
                onClick={() => setFilterIndustry(active ? "_all" : ind.key)}
                data-testid={`industry-pill-${ind.key}`}
                className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                  active
                    ? `${colorMap.split(" ").slice(0,3).join(" ")} ring-2 ring-offset-1 ring-offset-background ${colorMap.split(" ").slice(-1)[0]}/40 shadow-sm`
                    : `${colorMap.split(" ").slice(0,4).join(" ")} hover:scale-[1.03]`
                }`}
              >
                <Icon size={12} className={active ? "" : "opacity-80"} />
                {ind.label}
                <span className={`text-[10px] ml-0.5 font-mono ${active ? "opacity-90" : "opacity-60"}`}>
                  {ind.count}
                </span>
              </button>
            );
          })}
          {filterIndustry !== "_all" && (
            <button
              onClick={() => setFilterIndustry("_all")}
              data-testid="industry-pill-clear"
              className="ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <XIcon size={10}/> zurücksetzen
            </button>
          )}
        </div>
      )}

      {/* Filters (Kanban + List only) */}
      {tab !== "today" && (
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Lead, Stadt, Adresse suchen…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="lead-search-input" />
          </div>
          {cityOptions.length > 0 && (
            <Select value={filterCity} onValueChange={setFilterCity}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="filter-city"><SelectValue placeholder="Alle Städte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Städte ({cityOptions.length})</SelectItem>
                {cityOptions.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filterScore} onValueChange={setFilterScore}>
            <SelectTrigger className="w-32 rounded-sm" data-testid="filter-score"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Alle Scores</SelectItem>
              <SelectItem value="hot"><span className="inline-flex items-center gap-1.5"><Flame size={12} className="text-red-600" /> HOT</span></SelectItem>
              <SelectItem value="warm"><span className="inline-flex items-center gap-1.5"><ThermometerSun size={12} className="text-amber-600" /> WARM</span></SelectItem>
              <SelectItem value="cold"><span className="inline-flex items-center gap-1.5"><Snowflake size={12} className="text-slate-500" /> COLD</span></SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterOwner} onValueChange={setFilterOwner}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="filter-owner"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Alle Owner</SelectItem>
              <SelectItem value="_mine">Meine Leads</SelectItem>
              <SelectItem value="_unassigned">Nicht zugewiesen</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          {tagsList.length > 0 && (
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="filter-tag"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Alle Tags</SelectItem>
                {tagsList.slice(0, 30).map(t => <SelectItem key={t.name} value={t.name}>{t.name} <span className="text-muted-foreground ml-1">({t.count})</span></SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {tab === "list" && (
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="filter-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Stages</SelectItem>
                {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-32 rounded-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="score">↓ Score</SelectItem>
              <SelectItem value="created_at">↓ Neueste</SelectItem>
              <SelectItem value="updated_at">↓ Zuletzt aktiv</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tab Content */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Laden...</p>
      ) : tab === "kanban" ? (
        leads.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-sm">
            <Users size={32} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Noch keine Leads — leg los!</p>
          </div>
        ) : (
          <PipelineKanban
            stages={stages}
            leads={filteredLeads}
            users={users}
            onCardClick={(id) => setDetailLeadId(id)}
            filterUserId={filterOwner}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            bulkMode={bulkMode}
          />
        )
      ) : tab === "list" ? (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="grid grid-cols-[auto_2fr_auto_1fr_1fr_1fr_auto] gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border px-4 py-2.5 bg-muted/30">
            <span className="w-5"></span>
            <span>Unternehmen / Kontakt</span><span className="w-12">Score</span><span>Stage</span><span>Owner</span><span>Quelle</span><span></span>
          </div>
          {filteredLeads.length === 0 && <div className="px-5 py-12 text-center"><Users size={28} className="mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">{search ? "Keine Treffer" : "Noch keine Leads"}</p></div>}
          {filteredLeads.map(l => {
            const st = stageById[l.stage_id];
            const ow = userById[l.assigned_to];
            const cat = l.score_category || "cold";
            const scoreColors = { hot: "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400", warm: "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400", cold: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" };
            const isSelected = selectedIds.has(l.id);
            return (
              <div
                key={l.id}
                onClick={() => bulkMode ? toggleSelect(l.id) : setDetailLeadId(l.id)}
                className={`grid grid-cols-[auto_2fr_auto_1fr_1fr_1fr_auto] gap-2 items-center px-4 py-3 border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer ${isSelected ? "bg-muted/40" : ""}`}
                data-testid={`lead-row-${l.id}`}
              >
                <div className="w-5 flex justify-center">
                  {bulkMode ? <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(l.id)} onClick={e => e.stopPropagation()} className="accent-foreground" /> : null}
                </div>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center text-xs font-bold shrink-0">{l.company_name?.[0]?.toUpperCase() || "?"}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{l.company_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{l.contact_name} {l.contact_email ? `· ${l.contact_email}` : ""}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded inline-flex items-center justify-center w-12 ${scoreColors[cat]}`}>
                  {l.score != null ? `${l.score}` : "—"}
                </span>
                {st ? (
                  <span className="text-xs font-semibold inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm w-fit" style={{ background: st.color + "20", color: st.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} /> {st.name}
                  </span>
                ) : <span className="text-xs text-muted-foreground">—</span>}
                <span className="text-xs">
                  {ow ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-foreground text-background flex items-center justify-center text-[9px] font-bold">{(ow.name || ow.email)[0].toUpperCase()}</span>
                      <span className="text-muted-foreground truncate">{ow.name || ow.email}</span>
                    </span>
                  ) : <span className="text-muted-foreground italic">—</span>}
                </span>
                <span className="text-xs text-muted-foreground capitalize">{l.source}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <span onClick={e => e.stopPropagation()} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground" data-testid={`lead-menu-${l.id}`}>
                      <MoreHorizontal size={14} />
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={e => e.stopPropagation()} className="rounded-sm">
                    <DropdownMenuItem onClick={() => setDetailLeadId(l.id)} className="flex items-center gap-2 text-sm"><ArrowRight size={13} /> Öffnen</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleDelete(l.id)} className="text-destructive flex items-center gap-2 text-sm"><Trash2 size={13} /> Löschen</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      ) : (
        <TodayView onLeadClick={(id) => setDetailLeadId(id)} />
      )}

      {/* Create Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Neuer Lead</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="create-lead-form">
            {/* Firmen-Block */}
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Firma</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Unternehmen *</Label>
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required className="rounded-sm" data-testid="lead-company-input" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Website</Label>
                  <Input
                    type="url"
                    placeholder="z. B. https://www.firma.de"
                    value={form.website}
                    onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                    className="rounded-sm"
                    data-testid="lead-website-input"
                  />
                  <p className="text-[10px] text-muted-foreground/70">Wenn vorhanden – sorgt für besseres Scraping &amp; bessere Demo-Generierung</p>
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Google Maps URL</Label>
                  <Input
                    type="url"
                    placeholder="z. B. https://www.google.com/maps/place/..."
                    value={form.google_maps_url}
                    onChange={e => setForm(f => ({ ...f, google_maps_url: e.target.value }))}
                    className="rounded-sm"
                    data-testid="lead-gmaps-input"
                  />
                  <p className="text-[10px] text-muted-foreground/70">Damit du den Standort direkt aus dem CRM auf Google Maps öffnen kannst</p>
                </div>
                {field("address", "Adresse")}
                {field("city", "Stadt")}
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Branche / Tags <span className="text-[10px] text-muted-foreground/60">(Komma-getrennt)</span></Label>
                  <Input
                    placeholder="z. B. friseur, dresden, premium"
                    value={form.tags}
                    onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                    className="rounded-sm"
                    data-testid="lead-tags-input"
                  />
                </div>
              </div>
            </div>
            {/* Kontakt-Block */}
            <div className="space-y-3 pt-2 border-t">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Kontakt</p>
              <div className="grid grid-cols-2 gap-3">
                {field("contact_name", "Ansprechpartner")}
                {field("contact_email", "E-Mail", "email")}
                {field("contact_phone", "Telefon", "tel")}
                {field("budget", "Budget")}
              </div>
            </div>
            {/* Quelle + Nachricht */}
            <div className="space-y-3 pt-2 border-t">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quelle</Label>
                  <Select value={form.source} onValueChange={v => setForm(f => ({ ...f, source: v }))}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manuell">Manuell</SelectItem>
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="empfehlung">Empfehlung</SelectItem>
                      <SelectItem value="kaltakquise">Kaltakquise</SelectItem>
                      <SelectItem value="csv_import">CSV-Import</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Nachricht / Anfrage</Label>
                <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="rounded-sm" rows={3} />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" disabled={saving} className="rounded-sm" data-testid="create-lead-submit-btn">{saving ? "..." : "Anlegen"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      {detailLeadId && (
        <LeadDetailModal
          leadId={detailLeadId}
          open={!!detailLeadId}
          onClose={() => setDetailLeadId(null)}
          onUpdate={reload}
          stages={stages}
          users={users}
          currentUser={user}
        />
      )}

      {/* CSV Import */}
      <CSVImportWizard
        open={showImport}
        onClose={() => setShowImport(false)}
        users={users}
        stages={stages}
        onDone={reload}
      />

      {/* Lost-Reason */}
      <LostReasonModal
        open={!!pendingLostMove}
        onClose={() => setPendingLostMove(null)}
        onConfirm={handleLostConfirmed}
      />
    </div>
  );
}
